/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-common
 * (C) Copyright IBM Corp. 2020
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import React, { Component } from 'react';
import { omit } from 'lodash';
import PropTypes from 'prop-types';
import { propsUtils } from '@ba-ui-toolkit/utils';
import { RadioButton as CarbonRadio } from 'carbon';
import styles from './Radio.scss';

const radioClassName = styles.radioClassName.replace(/"/g, '');

const { BaPropTypes } = propsUtils;

export default class Radio extends Component {
	static propTypes = {
		/**
		 * Specify whether the <RadioButton> is currently checked
		 */
		checked: PropTypes.bool,

		/**
		 * Provide an optional className to be applied to the containing node
		 */
		className: PropTypes.string,

		/**
		 * Specify whether the <RadioButton> should be checked by default
		 */
		defaultChecked: PropTypes.bool,

		/**
		 * Specify whether the control is disabled
		 */
		disabled: PropTypes.bool,

		/**
		 * Provide a unique id for the underlying <input> node
		 */
		id: PropTypes.string,

		/**
		 * Legacy toolkit prop, map to labelText
		 */
		label: PropTypes.node,

		/**
		 * Provide label text to be read by screen readers when interacting with the
		 * control
		 */
		labelText: PropTypes.node.isRequired,

		/**
		 * Specify whether the label should be hidden, or not
		 */
		hideLabel: PropTypes.bool,

		/**
		 * Provide where label text should be placed
		 * NOTE: `top`/`bottom` are deprecated
		 */
		labelPosition: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),

		/**
		 * Provide a name for the underlying <input> node
		 */
		name: PropTypes.string,

		/**
		 * Provide a handler that is invoked when a user clicks on the control
		 */
		onClick: PropTypes.func,

		/**
		 * Provide an optional `onChange` hook that is called each time the value of
		 * the underlying <input> changes
		 */
		onChange: PropTypes.func,

		/**
		 * pass through toolkit props
		 */
		style: PropTypes.object,

		/**
		 * Specify the value of the <RadioButton>
		 */
		value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

		// deprecated prop
		// eslint-disable-next-line react/no-unused-prop-types
		tabIndex: BaPropTypes.deprecatedByCarbon(CarbonRadio),
		// deprecated prop
		// eslint-disable-next-line react/no-unused-prop-types
		title: BaPropTypes.deprecatedByCarbon(CarbonRadio),
		// deprecated prop
		// eslint-disable-next-line react/no-unused-prop-types
		readOnly: BaPropTypes.deprecatedByCarbon(CarbonRadio),
		// deprecated prop
		// eslint-disable-next-line react/no-unused-prop-types
		hover: BaPropTypes.deprecatedByCarbon(CarbonRadio),
		// deprecated prop
		// eslint-disable-next-line react/no-unused-prop-types
		active: BaPropTypes.deprecatedByCarbon(CarbonRadio),
		// deprecated prop
		// eslint-disable-next-line react/no-unused-prop-types
		'aria-labelledby': BaPropTypes.deprecatedByCarbon(CarbonRadio),
		// deprecated prop
		// eslint-disable-next-line react/no-unused-prop-types
		align: BaPropTypes.deprecatedByCarbon(CarbonRadio),
	};

	render() {
		const { label, labelText, className, ...rest } = this.props;

		const passThrouProps = omit(rest, [
			'tabIndex',
			'title',
			'readOnly',
			'hover',
			'active',
			'aria-labelledby',
			'align',
		]);

		return (
			<CarbonRadio
				className={`${className} ${radioClassName}`}
				labelText={labelText || label}
				{...passThrouProps}
			/>
		);
	}
}
